import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaPlus, FaPrint, FaCheckCircle, FaExclamationTriangle, FaFilter } from 'react-icons/fa';
import './Powers.css';

const Powers = () => {
  const [statistics, setStatistics] = useState({
    total: 0,
    available: 0,
    assigned: 0,
    executed: 0,
    voided: 0
  });
  const [powers, setPowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [agents, setAgents] = useState([]);
  const [selectedPowers, setSelectedPowers] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignToAgentId, setAssignToAgentId] = useState('');
  const [showAddPowersModal, setShowAddPowersModal] = useState(false);
  const [powerRanges, setPowerRanges] = useState([{
    prefix: '',
    startNumber: '',
    endNumber: '',
    surety: 'Allegheny Casualty',
    receivedDate: '',
    expiresDate: ''
  }]);

  // Filter states
  const [filters, setFilters] = useState({
    defendantName: '',
    surety: '',
    prefix: '',
    agent: '',
    status: '',
    powerNumberFrom: '',
    powerNumberTo: '',
    addedDateFrom: '',
    addedDateTo: '',
    assignedDateFrom: '',
    assignedDateTo: '',
    receivedDateFrom: '',
    receivedDateTo: '',
    sortBy: 'added_date',
    orderBy: 'desc'
  });

  const fetchStatistics = async () => {
    try {
      const response = await axios.get('/powers/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('Failed to load statistics');
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/users');
      setAgents(response.data.filter(user => user.role === 'agent' || user.role === 'admin'));
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchPowers = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: itemsPerPage,
        sortBy: filters.sortBy,
        orderBy: filters.orderBy
      };

      // Add filters to params if they have values
      if (filters.defendantName) params.defendantName = filters.defendantName;
      if (filters.surety) params.surety = filters.surety;
      if (filters.prefix) params.prefix = filters.prefix;
      if (filters.agent) params.agentId = filters.agent;
      if (filters.status) params.status = filters.status;
      if (filters.powerNumberFrom) params.powerNumberFrom = filters.powerNumberFrom;
      if (filters.powerNumberTo) params.powerNumberTo = filters.powerNumberTo;
      if (filters.addedDateFrom) params.addedDateFrom = filters.addedDateFrom;
      if (filters.addedDateTo) params.addedDateTo = filters.addedDateTo;
      if (filters.assignedDateFrom) params.assignedDateFrom = filters.assignedDateFrom;
      if (filters.assignedDateTo) params.assignedDateTo = filters.assignedDateTo;
      if (filters.receivedDateFrom) params.receivedDateFrom = filters.receivedDateFrom;
      if (filters.receivedDateTo) params.receivedDateTo = filters.receivedDateTo;

      const response = await axios.get('/powers', { params });

      setPowers(response.data.powers || response.data);
      setTotalPages(response.data.totalPages || Math.ceil((response.data.total || powers.length) / itemsPerPage));
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching powers:', error);
      toast.error('Failed to load powers data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    fetchAgents();
    fetchPowers(currentPage);
  }, []);

  useEffect(() => {
    fetchPowers(currentPage);
  }, [currentPage, itemsPerPage]);

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'available':
        return 'status-badge status-available';
      case 'assigned':
        return 'status-badge status-assigned';
      case 'executed':
        return 'status-badge status-executed';
      case 'voided':
        return 'status-badge status-voided';
      default:
        return 'status-badge';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleAddPowers = () => {
    setShowAddPowersModal(true);
  };

  const handlePowerRangeChange = (index, field, value) => {
    const updatedRanges = [...powerRanges];
    updatedRanges[index][field] = value;
    setPowerRanges(updatedRanges);
  };

  const addPowerRange = () => {
    setPowerRanges([...powerRanges, {
      prefix: '',
      startNumber: '',
      endNumber: '',
      surety: 'Allegheny Casualty',
      receivedDate: '',
      expiresDate: ''
    }]);
  };

  const removePowerRange = (index) => {
    if (powerRanges.length > 1) {
      const updatedRanges = powerRanges.filter((_, i) => i !== index);
      setPowerRanges(updatedRanges);
    }
  };

  const calculateTotalPowers = () => {
    return powerRanges.reduce((total, range) => {
      if (range.startNumber && range.endNumber) {
        const start = parseInt(range.startNumber);
        const end = parseInt(range.endNumber);
        if (start <= end) {
          return total + (end - start + 1);
        }
      }
      return total;
    }, 0);
  };

  const handleSubmitPowers = async () => {
    // Validate ranges
    for (let i = 0; i < powerRanges.length; i++) {
      const range = powerRanges[i];
      if (!range.prefix || !range.startNumber || !range.endNumber) {
        toast.error(`Please fill in all fields for range ${i + 1}`);
        return;
      }
      if (parseInt(range.startNumber) > parseInt(range.endNumber)) {
        toast.error(`Start number must be less than or equal to end number for range ${i + 1}`);
        return;
      }
    }

    try {
      // Submit each range
      for (const range of powerRanges) {
        await axios.post('/powers', {
          prefix: range.prefix,
          startNumber: parseInt(range.startNumber),
          endNumber: parseInt(range.endNumber),
          surety: range.surety,
          receivedDate: range.receivedDate || null,
          expiresDate: range.expiresDate || null
        });
      }

      const totalAdded = calculateTotalPowers();
      toast.success(`Successfully added ${totalAdded} power(s)`);
      setShowAddPowersModal(false);
      setPowerRanges([{
        prefix: '',
        startNumber: '',
        endNumber: '',
        surety: 'Allegheny Casualty',
        receivedDate: '',
        expiresDate: ''
      }]);
      fetchStatistics();
      fetchPowers(currentPage);
    } catch (error) {
      console.error('Error adding powers:', error);
      toast.error(error.response?.data?.message || 'Failed to add powers');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPowers(1);
  };

  const handleReset = () => {
    setFilters({
      defendantName: '',
      surety: '',
      prefix: '',
      agent: '',
      status: '',
      powerNumberFrom: '',
      powerNumberTo: '',
      addedDateFrom: '',
      addedDateTo: '',
      assignedDateFrom: '',
      assignedDateTo: '',
      receivedDateFrom: '',
      receivedDateTo: '',
      sortBy: 'added_date',
      orderBy: 'desc'
    });
    setCurrentPage(1);
    setTimeout(() => fetchPowers(1), 0);
  };

  // Power prefix options
  const prefixOptions = ['AS1K', 'AS3K', 'AS6K', 'AS10K', 'AS25K', 'AS50K', 'AS100K'];

  // Surety options
  const suretyOptions = [
    'Allegheny Casualty',
    'Accredited Surety',
    'Lexon Insurance',
    'International Fidelity',
    'Other'
  ];

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedPowers(powers.map(p => p.id));
    } else {
      setSelectedPowers([]);
    }
  };

  const handleSelectPower = (powerId) => {
    setSelectedPowers(prev => {
      if (prev.includes(powerId)) {
        return prev.filter(id => id !== powerId);
      } else {
        return [...prev, powerId];
      }
    });
  };

  const handleBulkAssign = async () => {
    if (!assignToAgentId) {
      toast.error('Please select an agent');
      return;
    }

    if (selectedPowers.length === 0) {
      toast.error('Please select at least one power');
      return;
    }

    try {
      await axios.post('/powers/bulk-assign', {
        powerIds: selectedPowers,
        agentId: assignToAgentId
      });

      toast.success(`Successfully assigned ${selectedPowers.length} power(s) to agent`);
      setShowAssignModal(false);
      setSelectedPowers([]);
      setAssignToAgentId('');
      fetchStatistics();
      fetchPowers(currentPage);
    } catch (error) {
      console.error('Error assigning powers:', error);
      toast.error('Failed to assign powers');
    }
  };

  return (
    <div className="powers-container">
      <div className="powers-header">
        <h1>Powers of Attorney</h1>
      </div>

      <div className="statistics-dashboard">
        <div className="stat-card stat-total">
          <div className="stat-label">Total Powers</div>
          <div className="stat-value">{statistics.total}</div>
        </div>
        <div className="stat-card stat-available">
          <div className="stat-label">Available</div>
          <div className="stat-value">{statistics.available}</div>
        </div>
        <div className="stat-card stat-assigned">
          <div className="stat-label">Assigned</div>
          <div className="stat-value">{statistics.assigned}</div>
        </div>
        <div className="stat-card stat-executed">
          <div className="stat-label">Executed</div>
          <div className="stat-value">{statistics.executed}</div>
        </div>
        <div className="stat-card stat-voided">
          <div className="stat-label">Voided</div>
          <div className="stat-value">{statistics.voided}</div>
        </div>
      </div>

      <div className="filters-section">
        <div className="filters-header">
          <h3><FaFilter /> Filters</h3>
        </div>

        <div className="filters-grid">
          <div className="filter-row">
            <div className="filter-field">
              <label>Defendant Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by defendant name..."
                value={filters.defendantName}
                onChange={(e) => handleFilterChange('defendantName', e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label>Surety / Cash License</label>
              <select
                className="form-control"
                value={filters.surety}
                onChange={(e) => handleFilterChange('surety', e.target.value)}
              >
                <option value="">All Sureties</option>
                {suretyOptions.map(surety => (
                  <option key={surety} value={surety}>{surety}</option>
                ))}
              </select>
            </div>

            <div className="filter-field">
              <label>Prefix</label>
              <select
                className="form-control"
                value={filters.prefix}
                onChange={(e) => handleFilterChange('prefix', e.target.value)}
              >
                <option value="">All Prefixes</option>
                {prefixOptions.map(prefix => (
                  <option key={prefix} value={prefix}>{prefix}</option>
                ))}
              </select>
            </div>

            <div className="filter-field">
              <label>Assigned Agent</label>
              <select
                className="form-control"
                value={filters.agent}
                onChange={(e) => handleFilterChange('agent', e.target.value)}
              >
                <option value="">All Agents</option>
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>{agent.username}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-field">
              <label>Status</label>
              <select
                className="form-control"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="available">Available</option>
                <option value="assigned">Assigned</option>
                <option value="executed">Executed</option>
                <option value="voided">Voided</option>
              </select>
            </div>

            <div className="filter-field">
              <label>Power Number From</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., 1000"
                value={filters.powerNumberFrom}
                onChange={(e) => handleFilterChange('powerNumberFrom', e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label>Power Number To</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., 2000"
                value={filters.powerNumberTo}
                onChange={(e) => handleFilterChange('powerNumberTo', e.target.value)}
              />
            </div>

            <div className="filter-field">
              <label>Sort By</label>
              <select
                className="form-control"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="added_date">Added Date</option>
                <option value="power_number">Power Number</option>
                <option value="accepted_date">Assigned Date</option>
                <option value="posted_date">Posted Date</option>
                <option value="received_date">Received Date</option>
                <option value="expires_date">Expiration Date</option>
              </select>
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-field filter-field-small">
              <label>Added Date From</label>
              <input
                type="date"
                className="form-control"
                value={filters.addedDateFrom}
                onChange={(e) => handleFilterChange('addedDateFrom', e.target.value)}
              />
            </div>

            <div className="filter-field filter-field-small">
              <label>Added Date To</label>
              <input
                type="date"
                className="form-control"
                value={filters.addedDateTo}
                onChange={(e) => handleFilterChange('addedDateTo', e.target.value)}
              />
            </div>

            <div className="filter-field filter-field-small">
              <label>Assigned Date From</label>
              <input
                type="date"
                className="form-control"
                value={filters.assignedDateFrom}
                onChange={(e) => handleFilterChange('assignedDateFrom', e.target.value)}
              />
            </div>

            <div className="filter-field filter-field-small">
              <label>Assigned Date To</label>
              <input
                type="date"
                className="form-control"
                value={filters.assignedDateTo}
                onChange={(e) => handleFilterChange('assignedDateTo', e.target.value)}
              />
            </div>

            <div className="filter-field filter-field-small">
              <label>Received Date From</label>
              <input
                type="date"
                className="form-control"
                value={filters.receivedDateFrom}
                onChange={(e) => handleFilterChange('receivedDateFrom', e.target.value)}
              />
            </div>

            <div className="filter-field filter-field-small">
              <label>Received Date To</label>
              <input
                type="date"
                className="form-control"
                value={filters.receivedDateTo}
                onChange={(e) => handleFilterChange('receivedDateTo', e.target.value)}
              />
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-field">
              <label>Order By</label>
              <select
                className="form-control"
                value={filters.orderBy}
                onChange={(e) => handleFilterChange('orderBy', e.target.value)}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>

            <div className="filter-field">
              <label>Results Per Page</label>
              <select
                className="form-control"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>

            <div className="filter-actions">
              <button className="btn btn-primary" onClick={handleSearch}>
                SEARCH
              </button>
              <button className="btn btn-secondary" onClick={handleReset}>
                RESET
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="action-bar">
        <button className="btn btn-primary" onClick={handleAddPowers}>
          <FaPlus /> Add Powers
        </button>
        {selectedPowers.length > 0 && (
          <button className="btn btn-success" onClick={() => setShowAssignModal(true)}>
            Assign {selectedPowers.length} Power(s) to Agent
          </button>
        )}
        <button className="btn btn-secondary" onClick={handlePrint}>
          <FaPrint /> Print
        </button>
      </div>

      <div className="powers-table-container">
        {loading ? (
          <div className="loading-state">Loading powers...</div>
        ) : powers.length === 0 ? (
          <div className="empty-state">No powers found</div>
        ) : (
          <table className="powers-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={selectedPowers.length === powers.length && powers.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Assigned Agent</th>
                <th>Power #</th>
                <th>Posted Date</th>
                <th>Surety</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Defendant</th>
                <th>Received Date</th>
                <th>Expires Date</th>
                <th>Receipt Status</th>
                <th>Added Date</th>
              </tr>
            </thead>
            <tbody>
              {powers.map((power) => (
                <tr key={power.id || power.power_number}>
                  <td className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectedPowers.includes(power.id)}
                      onChange={() => handleSelectPower(power.id)}
                    />
                  </td>
                  <td>
                    {power.assigned_agent ? (
                      <div className="agent-info">
                        <div className="agent-name">{power.assigned_agent}</div>
                        {power.accepted_date && (
                          <div className="accepted-date">
                            Accepted: {formatDate(power.accepted_date)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="not-assigned">Not Assigned</span>
                    )}
                  </td>
                  <td className="power-number">{power.power_number || 'N/A'}</td>
                  <td>{formatDate(power.posted_date)}</td>
                  <td>{power.surety || 'Allegheny Casualty'}</td>
                  <td className="amount">{formatCurrency(power.amount)}</td>
                  <td>
                    <span className={getStatusBadgeClass(power.status)}>
                      {power.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </td>
                  <td>{power.defendant || 'N/A'}</td>
                  <td>{formatDate(power.received_date)}</td>
                  <td>{formatDate(power.expires_date)}</td>
                  <td>
                    <div className="receipt-status">
                      {power.receipt_status === 'UPLOADED' ? (
                        <span className="receipt-uploaded">
                          <FaCheckCircle /> UPLOADED
                        </span>
                      ) : (
                        <span className="receipt-missing">
                          <FaExclamationTriangle /> MISSING
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(power.added_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && powers.length > 0 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Powers to Agent</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Assign {selectedPowers.length} selected power(s) to an agent:</p>
              <div className="form-group">
                <label>Select Agent</label>
                <select
                  className="form-control"
                  value={assignToAgentId}
                  onChange={(e) => setAssignToAgentId(e.target.value)}
                >
                  <option value="">Choose an agent...</option>
                  {agents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.username} ({agent.full_name || agent.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleBulkAssign}>
                Assign Powers
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddPowersModal && (
        <div className="modal-overlay" onClick={() => setShowAddPowersModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Powers of Attorney</h2>
              <button className="close-btn" onClick={() => setShowAddPowersModal(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Add power ranges by specifying prefix and start/end numbers. You can add multiple ranges at once.</p>

              {powerRanges.map((range, index) => (
                <div key={index} className="power-range-row">
                  <h4>Range {index + 1}</h4>
                  <div className="range-fields">
                    <div className="form-group">
                      <label>Prefix *</label>
                      <select
                        className="form-control"
                        value={range.prefix}
                        onChange={(e) => handlePowerRangeChange(index, 'prefix', e.target.value)}
                      >
                        <option value="">Select Prefix...</option>
                        {prefixOptions.map(prefix => (
                          <option key={prefix} value={prefix}>{prefix}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Start Number *</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g., 1000"
                        value={range.startNumber}
                        onChange={(e) => handlePowerRangeChange(index, 'startNumber', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>End Number *</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g., 1100"
                        value={range.endNumber}
                        onChange={(e) => handlePowerRangeChange(index, 'endNumber', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Quantity</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          range.startNumber && range.endNumber && parseInt(range.startNumber) <= parseInt(range.endNumber)
                            ? (parseInt(range.endNumber) - parseInt(range.startNumber) + 1)
                            : '0'
                        }
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label>Surety Company</label>
                      <select
                        className="form-control"
                        value={range.surety}
                        onChange={(e) => handlePowerRangeChange(index, 'surety', e.target.value)}
                      >
                        {suretyOptions.map(surety => (
                          <option key={surety} value={surety}>{surety}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Received Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={range.receivedDate}
                        onChange={(e) => handlePowerRangeChange(index, 'receivedDate', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Expires Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={range.expiresDate}
                        onChange={(e) => handlePowerRangeChange(index, 'expiresDate', e.target.value)}
                      />
                    </div>

                    {powerRanges.length > 1 && (
                      <div className="form-group">
                        <label>&nbsp;</label>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => removePowerRange(index)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <button className="btn btn-secondary btn-sm" onClick={addPowerRange}>
                <FaPlus /> Add Another Range
              </button>

              <div className="total-powers-info">
                <strong>Total Powers to Add: {calculateTotalPowers()}</strong>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowAddPowersModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSubmitPowers}>
                Add {calculateTotalPowers()} Power(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Powers;
